import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Edit, Trash2, KeyRound, User, Building2, Heart, Share2, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsersPage() {
  const { branches: authBranches } = useAuth();
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    nome: "",
    email: "",
    senha: "",
    perfil_acesso_id: "",
    filiais_ids: []
  });

  useEffect(() => {
    loadData();
  }, []);

  // Use branches from auth context or local state
  const allBranches = branches.length > 0 ? branches : authBranches;

  const loadData = async () => {
    try {
      const [usersRes, profilesRes, branchesRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/access-profiles`),
        axios.get(`${API}/branches`)
      ]);
      setUsers(usersRes.data);
      setProfiles(profilesRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        perfil_acesso_id: form.perfil_acesso_id || null,
        filial_id: form.filiais_ids.length > 0 ? form.filiais_ids[0] : null,
        filiais_ids: form.filiais_ids
      };
      
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, payload);
        // Atualizar unidades separadamente
        await axios.put(`${API}/users/${editingUser.id}/branches`, {
          filial_id: payload.filial_id,
          filiais_ids: payload.filiais_ids
        });
        toast.success("Usuário atualizado!");
      } else {
        const res = await axios.post(`${API}/auth/register`, payload);
        // Atualizar unidades do novo usuário
        if (res.data.id && form.filiais_ids.length > 0) {
          await axios.put(`${API}/users/${res.data.id}/branches`, {
            filial_id: payload.filial_id,
            filiais_ids: payload.filiais_ids
          });
        }
        toast.success("Usuário criado!");
      }
      setDialogOpen(false);
      setEditingUser(null);
      setForm({ nome: "", email: "", senha: "", perfil_acesso_id: "", filiais_ids: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({
      nome: user.nome,
      email: user.email,
      senha: "",
      perfil_acesso_id: user.perfil_acesso_id || "",
      filiais_ids: user.filiais_ids || (user.filial_id ? [user.filial_id] : [])
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja remover este usuário?")) return;
    try {
      await axios.delete(`${API}/users/${id}`);
      toast.success("Usuário removido");
      loadData();
    } catch (error) {
      toast.error("Erro ao remover");
    }
  };

  const toggleBranch = (branchId) => {
    setForm(prev => {
      const current = prev.filiais_ids || [];
      if (current.includes(branchId)) {
        return { ...prev, filiais_ids: current.filter(id => id !== branchId) };
      } else {
        return { ...prev, filiais_ids: [...current, branchId] };
      }
    });
  };

  const getProfileName = (profileId) => {
    if (!profileId) return null;
    
    // First, try to find in loaded profiles
    const profile = profiles.find(p => p.id === profileId);
    if (profile?.nome) return profile.nome;
    
    // If profiles not loaded yet but we have an ID, show loading indicator
    // instead of showing the raw ID
    if (profiles.length === 0 && profileId) {
      return "Carregando...";
    }
    
    // Profile not found in list - don't show raw ID
    return null;
  };

  const getBranchNames = (user) => {
    const ids = user.filiais_ids || (user.filial_id ? [user.filial_id] : []);
    
    // If no branches loaded yet, show loading
    if (allBranches.length === 0 && ids.length > 0) {
      return ["Carregando..."];
    }
    
    return ids.map(id => {
      const branch = allBranches.find(b => b.id === id);
      // Return branch name if found, otherwise null (don't show raw ID)
      return branch?.nome || null;
    }).filter(name => name !== null);
  };

  // Filter users by search
  const filteredUsers = users.filter(user => {
    const searchLower = search.toLowerCase();
    const profileName = getProfileName(user.perfil_acesso_id)?.toLowerCase() || "";
    return (
      user.nome?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      profileName.includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="usuarios-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Usuários</h1>
        <Button onClick={() => { setEditingUser(null); setForm({ nome: "", email: "", senha: "", perfil_acesso_id: "", filiais_ids: [] }); setDialogOpen(true); }} data-testid="add-user-button">
          <Plus className="h-4 w-4 mr-2" />
          Adicionar
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder="Buscar usuário por nome, email ou perfil..."
          className="pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="search-users"
        />
      </div>

      <div className="space-y-4">
        {filteredUsers.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">Nenhum usuário encontrado</p>
        ) : (
          filteredUsers.map((user) => {
            const profileName = getProfileName(user.perfil_acesso_id);
            const branchNames = getBranchNames(user);
            
            return (
              <Card key={user.id} className="card-hover" data-testid={`user-card-${user.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-semibold">
                          {user.nome?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{user.nome}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {profileName ? (
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <KeyRound className="h-3 w-3" />
                          {profileName}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="flex items-center gap-1 text-muted-foreground">
                          <User className="h-3 w-3" />
                          Sem perfil
                        </Badge>
                      )}
                      
                      <div className="flex gap-1 ml-2">
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(user)} data-testid={`edit-user-${user.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDelete(user.id)} data-testid={`delete-user-${user.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Mostrar unidades do usuário */}
                  {branchNames.length > 0 && (
                    <div className="mt-3 pt-3 border-t flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        Unidades:
                      </span>
                      {branchNames.map((name, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {name}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingUser ? "Editar Usuário" : "Novo Usuário"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                required
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                data-testid="user-nome"
              />
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="user-email"
              />
            </div>

            <div className="space-y-2">
              <Label>{editingUser ? "Nova Senha (deixe em branco para manter)" : "Senha *"}</Label>
              <Input
                type="password"
                required={!editingUser}
                value={form.senha}
                onChange={(e) => setForm({ ...form, senha: e.target.value })}
                data-testid="user-senha"
              />
            </div>

            <div className="space-y-2">
              <Label>Perfil de Acesso *</Label>
              <Select
                value={form.perfil_acesso_id || "none"}
                onValueChange={(v) => setForm({ ...form, perfil_acesso_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="user-perfil">
                  <SelectValue placeholder="Selecione um perfil" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-muted-foreground">Selecione um perfil</span>
                  </SelectItem>
                  {profiles.map((profile) => (
                    <SelectItem key={profile.id} value={profile.id}>
                      <div className="flex items-center gap-2">
                        <KeyRound className="h-4 w-4 text-primary" />
                        <span>{profile.nome || 'Perfil sem nome'}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Define as permissões do usuário</p>
            </div>

            {/* Seleção de Unidades */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Unidades com Acesso
              </Label>
              <p className="text-xs text-muted-foreground mb-2">
                Selecione as unidades que o usuário poderá acessar
              </p>
              
              <ScrollArea className="h-32 border rounded-md p-2">
                <div className="space-y-2">
                  {allBranches.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Nenhuma unidade cadastrada
                    </p>
                  ) : (
                    allBranches.map((branch) => {
                      const isSelected = form.filiais_ids?.includes(branch.id);
                      return (
                        <label
                          key={branch.id}
                          className={`flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-muted ${
                            isSelected ? 'bg-muted' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleBranch(branch.id)}
                            className="h-4 w-4 rounded border-primary text-primary focus:ring-primary"
                          />
                          <div className="flex items-center gap-2 flex-1">
                            {branch.tipo === "instituto_social" ? (
                              <Heart className="h-4 w-4 text-pink-500" />
                            ) : (
                              <Building2 className="h-4 w-4" />
                            )}
                            <span className="text-sm">{branch.nome}</span>
                            {branch.tipo === "instituto_social" && (
                              <Badge variant="secondary" className="text-xs">Instituto</Badge>
                            )}
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
              
              {form.filiais_ids?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {form.filiais_ids.map(id => {
                    const branch = allBranches.find(b => b.id === id);
                    return branch ? (
                      <Badge key={id} variant="secondary" className="text-xs">
                        {branch.nome}
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1" data-testid="save-user-button">
                Salvar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
